import React, { useEffect, useState } from "react";
import { api, CURRENCY_OPTIONS, fmtCur, fmtNum } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { Link } from "react-router-dom";

export default function StockMaster() {
  const [stocks, setStocks] = useState([]);
  const [totals, setTotals] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newStock, setNewStock] = useState({ symbol: "", name: "", market_price: "", currency: "RM" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ original_symbol: "", symbol: "", name: "", price: "", currency: "RM" });

  const load = async () => {
    const [s, t] = await Promise.all([api.get("/stocks"), api.get("/dashboard/stock-totals")]);
    setStocks(s.data);
    setTotals(t.data);
  };
  useEffect(() => { load(); }, []);

  const rows = stocks.map(s => {
    const t = totals.find(x => x.symbol === s.symbol) || {};
    return { ...s, ...t, currency: s.currency || "RM" };
  });

  const addStock = async () => {
    if (!newStock.symbol.trim()) return toast.error("Symbol required");
    try {
      await api.post("/stocks", {
        symbol: newStock.symbol,
        name: newStock.name,
        market_price: parseFloat(newStock.market_price) || 0,
        currency: newStock.currency,
      });
      toast.success(`${newStock.symbol.toUpperCase()} added (${newStock.currency})`);
      setAddOpen(false);
      setNewStock({ symbol: "", name: "", market_price: "", currency: "RM" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async () => {
    const p = parseFloat(editForm.price);
    if (isNaN(p) || p < 0) return toast.error("Enter valid price");
    if (!editForm.symbol.trim()) return toast.error("Symbol required");
    const newSymbol = editForm.symbol.toUpperCase().trim();
    try {
      await api.put(`/stocks/${editForm.original_symbol}`, {
        symbol: newSymbol,
        name: editForm.name,
        market_price: p,
        currency: editForm.currency,
      });
      toast.success(`${newSymbol} updated`);
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Registry</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">Stock Master</h1>
          <p className="text-slate-400 text-sm mt-2">Each stock has its own currency — every trade & report using that stock will display in the stock's currency automatically.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-stock" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"><Plus size={16} className="mr-2" /> Add Stock</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0E1524] border-slate-800 text-white">
            <DialogHeader><DialogTitle className="font-heading">Add Stock</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <Field label="Symbol *"><Input value={newStock.symbol} onChange={e => setNewStock({ ...newStock, symbol: e.target.value.toUpperCase() })} placeholder="AAPL" data-testid="form-stock-symbol-input" className="uppercase font-mono-tabular" /></Field>
              <Field label="Company Name"><Input value={newStock.name} onChange={e => setNewStock({ ...newStock, name: e.target.value })} placeholder="Apple Inc." data-testid="form-stock-name-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Currency *">
                  <Select value={newStock.currency} onValueChange={v => setNewStock({ ...newStock, currency: v })}>
                    <SelectTrigger data-testid="form-stock-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                      {CURRENCY_OPTIONS.map(c => <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Current Market Price">
                  <Input type="number" step="any" value={newStock.market_price} onChange={e => setNewStock({ ...newStock, market_price: e.target.value })} placeholder="175.50" data-testid="form-stock-price-input" className="font-mono-tabular" />
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
              <Button onClick={addStock} data-testid="form-stock-submit-button" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="card-tactical p-6">
        {rows.length === 0 ? (
          <div className="py-16 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">No stocks yet. Add one to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tactical" data-testid="table-stock-holdings-summary">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Name</th>
                  <th>Currency</th>
                  <th className="!text-right">Market Price</th>
                  <th className="!text-right">Total Shares</th>
                  <th className="!text-right">Total Value</th>
                  <th className="!text-right">Holders</th>
                  <th className="!text-right">Unrealized P&L</th>
                  <th className="!text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.symbol}>
                    <td>
                      <Link to={`/shareholding?symbol=${s.symbol}`} className="font-mono-tabular font-bold text-emerald-400 hover:underline">{s.symbol}</Link>
                    </td>
                    <td className="text-slate-300">{s.name || s.stock_name || "—"}</td>
                    <td><span className="px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/15 text-sky-300 border border-sky-500/30 font-mono-tabular">{s.currency}</span></td>
                    <td className="!text-right font-mono-tabular">{fmtCur(s.market_price, s.currency, 2)}</td>
                    <td className="!text-right font-mono-tabular">{fmtNum(s.total_quantity || 0, 2)}</td>
                    <td className="!text-right font-mono-tabular">{fmtCur(s.total_value || 0, s.currency)}</td>
                    <td className="!text-right font-mono-tabular text-slate-400">{s.num_holders || 0}</td>
                    <td className={`!text-right font-mono-tabular ${(s.unrealized_pnl || 0) >= 0 ? "ticker-up" : "ticker-down"}`}>{fmtCur(s.unrealized_pnl || 0, s.currency)}</td>
                    <td className="!text-right">
                      <Button size="sm" variant="outline" data-testid={`btn-update-price-${s.symbol}`} onClick={() => { setEditForm({ original_symbol: s.symbol, symbol: s.symbol, name: s.name || "", price: s.market_price, currency: s.currency || "RM" }); setEditOpen(true); }} className="border-slate-700 text-slate-700 hover:text-emerald-400"><Pencil size={12} className="mr-1" /> Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0E1524] border-slate-800 text-white">
          <DialogHeader><DialogTitle className="font-heading">Edit Stock · {editForm.original_symbol}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Symbol *">
                <Input
                  value={editForm.symbol}
                  onChange={e => setEditForm({ ...editForm, symbol: e.target.value.toUpperCase() })}
                  data-testid="edit-stock-symbol-input"
                  className="uppercase font-mono-tabular"
                />
              </Field>
              <Field label="Currency">
                <Select value={editForm.currency} onValueChange={v => setEditForm({ ...editForm, currency: v })}>
                  <SelectTrigger data-testid="edit-stock-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                    {CURRENCY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Company Name">
              <Input
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="e.g. Apple Inc."
                data-testid="edit-stock-name-input"
              />
            </Field>
            <Field label="Market Price">
              <Input type="number" step="any" value={editForm.price} onChange={e => setEditForm({ ...editForm, price: e.target.value })} data-testid="input-stock-market-price-edit" className="font-mono-tabular" />
            </Field>
            <p className="text-xs text-amber-400">Changing symbol, name or currency updates all historical transactions of {editForm.original_symbol} so reports stay consistent.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
            <Button onClick={saveEdit} data-testid="btn-confirm-price-update" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="label-caps !text-slate-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
